import React from 'react';
import { Card } from '../components/ui/card';
import { CheckCircle2, Zap, Star, Mail, Phone, Globe } from 'lucide-react';

const SalesPitch = () => {
  const pricingPlans = [
    {
      name: 'Starter',
      price: '$199',
      period: '/month',
      description: 'Perfect for single-location pet care facilities',
      features: [
        '1 location',
        'Up to 3 users',
        'Gmail integration',
        'Lead management',
        'POS matching',
        'Basic AI insights (50 queries/month)',
        'Email support'
      ],
      highlighted: false,
      icon: Zap
    },
    {
      name: 'Professional',
      price: '$499',
      period: '/month',
      description: 'Best for growing multi-location businesses',
      features: [
        '5 locations',
        'Unlimited users',
        'Gmail + Gingr integration',
        'Advanced lead tracking',
        'Unlimited AI insights',
        'Daily occupancy reports',
        'Revenue analytics',
        'Priority support'
      ],
      highlighted: true,
      icon: Star
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'pricing',
      description: 'Tailored solutions for large operations',
      features: [
        '10+ locations',
        'Custom integrations',
        'White-label option',
        'Dedicated account manager',
        'Custom AI training',
        'API access',
        'SLA guarantee',
        '24/7 phone support'
      ],
      highlighted: false,
      icon: Star
    }
  ];

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-8 md:p-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
          Transform Your Lead Management
        </h1>
        <p className="text-xl md:text-2xl mb-6 opacity-90">
          Get Real-Time Business Insights with AI
        </p>
        <p className="text-lg max-w-3xl">
          Stop losing leads in email chaos. Start making data-driven decisions with instant answers to your business questions.
        </p>
      </div>

      {/* Problem Section */}
      <Card className="p-8">
        <h2 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Manrope' }}>
          The Problem
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
              <div>
                <h3 className="font-semibold text-lg">Lost Leads in Email Chaos</h3>
                <p className="text-gray-600">Inquiries buried in inbox, follow-ups forgotten</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
              <div>
                <h3 className="font-semibold text-lg">No Visibility into POS Data</h3>
                <p className="text-gray-600">Can't quickly answer "How many baths today?"</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
              <div>
                <h3 className="font-semibold text-lg">Manual Tracking = Missed Revenue</h3>
                <p className="text-gray-600">No way to track which leads converted</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
              <div>
                <h3 className="font-semibold text-lg">Time-Consuming Reports</h3>
                <p className="text-gray-600">Hours spent pulling numbers from Gingr POS</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Solution Highlights */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-6 hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
            <Mail className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold mb-3">Lead Management</h3>
          <p className="text-gray-600">
            Automatic email ingestion, smart deduplication, POS matching with lifetime revenue tracking
          </p>
        </Card>

        <Card className="p-6 hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
            <Zap className="h-6 w-6 text-purple-600" />
          </div>
          <h3 className="text-xl font-bold mb-3">AI-Powered Insights</h3>
          <p className="text-gray-600">
            Ask questions in plain English, get instant answers about baths, revenue, occupancy
          </p>
        </Card>

        <Card className="p-6 hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-xl font-bold mb-3">Seamless Integration</h3>
          <p className="text-gray-600">
            Works with Gmail and Gingr POS. No complex setup, start capturing leads immediately
          </p>
        </Card>
      </div>

      {/* Key Features */}
      <Card className="p-8">
        <h2 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Manrope' }}>
          Key Features
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { title: 'Daily Bath Count', desc: 'Know exactly how many baths/grooming today' },
            { title: 'Occupancy Planning', desc: 'Check-ins, check-outs, overnight breakdown' },
            { title: 'Revenue Tracking', desc: 'Real-time revenue from Gingr invoices' },
            { title: 'Lead Tracking', desc: 'Every inquiry captured automatically' },
            { title: 'POS Matching', desc: 'See which leads became paying customers' },
            { title: 'AI Business Insights', desc: 'Ask any question about your operations' }
          ].map((feature, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Pricing Section */}
      <div id="pricing">
        <h2 className="text-3xl font-bold mb-2 text-center text-gray-900" style={{ fontFamily: 'Manrope' }}>
          Simple, Transparent Pricing
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Choose the plan that fits your business needs
        </p>

        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {pricingPlans.map((plan, idx) => {
            const IconComponent = plan.icon;
            return (
              <Card
                key={idx}
                className={`p-6 relative ${
                  plan.highlighted
                    ? 'border-2 border-blue-600 shadow-xl'
                    : 'border border-gray-200'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-2">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                  <p className="text-sm text-gray-600">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.name === 'Enterprise' ? 'Contact Sales' : 'Get Started'}
                </button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Business Impact */}
      <Card className="p-8 bg-gradient-to-br from-blue-50 to-purple-50">
        <h2 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Manrope' }}>
          Business Impact
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-600 mb-2">3 hours/day</div>
            <div className="text-gray-600">Time Saved</div>
            <p className="text-sm text-gray-500 mt-2">No manual tracking or report generation</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-purple-600 mb-2">35% → 52%</div>
            <div className="text-gray-600">Conversion Rate</div>
            <p className="text-sm text-gray-500 mt-2">Better follow-up, fewer lost leads</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">2 seconds</div>
            <div className="text-gray-600">Answer Time</div>
            <p className="text-sm text-gray-500 mt-2">Instant answers vs hours of reports</p>
          </div>
        </div>
      </Card>

      {/* CTA Section */}
      <Card className="p-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Business?</h2>
        <p className="text-xl mb-6 opacity-90">
          Schedule a demo or start your 14-day free trial today
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
            Schedule Demo
          </button>
          <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors">
            Start Free Trial
          </button>
        </div>
      </Card>

      {/* Footer */}
      <div className="text-center text-gray-600 text-sm py-4">
        <p>Built by <span className="font-semibold">Ariya Ventures Inc</span></p>
        <p className="mt-1">Custom software for modern businesses</p>
      </div>
    </div>
  );
};

export default SalesPitch;
